import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Dialog, Snackbar } from "@mui/material";
import "./qualifying.scss";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import {
  fetchCandidateQualifyQuestions,
  getPreScreenDetails,
  submitCandidateQualifyAnswers,
} from "../../Redux/ActionCreators/qualifyQuestionsAction";

export default function QualifyQuestions() {
  const { preScreenId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const [screenLoading, setScreenLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [answeringCompleted, setAnswerCompleted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [unanswered, setUnanswered] = useState({});

  const job = useSelector((state) => state.jobs.latestJob);
  const qualifyQuestions = useSelector(
    (state) => state.qualifyQuestions?.candidateQualifyQuestions
  );
  const qualifyingDetails = useSelector(
    (state) => state.qualifyQuestions?.preQualifyingDetails
  );

  const QUESTION_TYPE_ID_TO_LABEL = {
    "4c524f2f-48de-493e-894f-4f0bd931b4c9": "MCQ",
    "64468292-d741-4e52-893f-0846dd56830d": "Numeric",
    "4ffc711b-2f10-470b-b162-85f2a2f1e220": "Yes/No",
    "30afb159-85d7-490d-afcd-1af9497531ae": "Free Text",
  };

  useEffect(() => {
    const fetchPreScreen = async () => {
      setScreenLoading(true);
      try {
        await dispatch(getPreScreenDetails(preScreenId));
      } catch (error) {
        const status = error?.parsedStatus || error?.response?.status;
        if (status === 403 || status === 410) setLinkExpired(true);
        console.error("Error loading pre-screen details:", error);
      }
      setScreenLoading(false);
    };

    fetchPreScreen();
  }, [preScreenId, dispatch]);

  useEffect(() => {
    const fetchJobAndQuestions = async () => {
      if (!qualifyingDetails?.job_id || !qualifyingDetails?.job_candidate_id) return;

      setDetailsLoading(true);
      try {
        await dispatch(fetchJobById(qualifyingDetails.job_id));
        await dispatch(
          fetchCandidateQualifyQuestions(qualifyingDetails.job_candidate_id, preScreenId)
        );
      } catch (error) {
        console.error("Error loading job or questions:", error);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchJobAndQuestions();
  }, [qualifyingDetails, preScreenId, dispatch]);

  const snackClosed = () => {
    setSuccessMessage(false);
    setSuccessMessageText("");
  };

  const handleOptionChange = (questionId, optionId, answerText = "") => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        selected_option_id: optionId || null,
        answer_text: answerText || "",
        score: 0,
      },
    }));
    setUnanswered((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  };

  const submitAnswers = async () => {
    let allAnswered = true;
      let newUnanswered = {};

      for (let q of qualifyQuestions) {
        const ans = answers[q.id];
        const type = QUESTION_TYPE_ID_TO_LABEL[q.question_type_id];

        if (type === "MCQ" || type === "Yes/No") {
          if (!ans || !ans.selected_option_id) {
            allAnswered = false;
            newUnanswered[q.id] = "This question is required";
          }
        } else if (type === "Numeric") {
          if (!ans || ans.answer_text.trim() === "") {
            allAnswered = false;
            newUnanswered[q.id] = "This question is required";
          }
        } else {
          if (
            !ans ||
            (!ans.selected_option_id && ans.answer_text.trim() === "")
          ) {
            allAnswered = false;
            newUnanswered[q.id] = "This question is required";
          }
        }
      }

      setUnanswered(newUnanswered);

      if (!allAnswered) {
        setSnackbarSeverity("error");
        setSuccessMessageText("Please answer all the pre quaifying questions");
        setSuccessMessage(true);
        return;
      }

      const answersArray = qualifyQuestions.map((q) => {
        const ans = answers[q.id];
        const type = QUESTION_TYPE_ID_TO_LABEL[q.question_type_id];

        if (type === "MCQ" || type === "Yes/No") {
          return {
            question_id: q.id,
            selected_option_id: ans?.selected_option_id || null,
            score: 0,
          };
        }

        if (type === "Numeric") {
          return {
            question_id: q.id,
            answer_text: ans?.answer_text || "",
            score: 0,
          };
        }

        return {
          question_id: q.id,
          selected_option_id: ans?.selected_option_id || null,
          answer_text: ans?.answer_text || "",
          score: 0,
        };
      });

      setLoading(true);
      const success = await dispatch(
        submitCandidateQualifyAnswers(
          qualifyingDetails.job_candidate_id,
          preScreenId,
          answersArray
        )
      );
      setLoading(false);

      if (success) {
        setAnswerCompleted(true);
      } else {
        setSnackbarSeverity("error");
        setSuccessMessageText("Failed to submit answers");
        setSuccessMessage(true);
      }
  };

  if (screenLoading || detailsLoading) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <Dialog
        className="schedule-loader"
        open={loading}
        onClose={() => {}}
        PaperProps={{
          sx: {
            boxShadow: "none",
          },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={successMessage}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert
          onClose={snackClosed}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {successMessageText}
        </Alert>
      </Snackbar>
      {linkExpired ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "24px",
            color: "#ff0000",
          }}
        >
          Link has been expired
        </div>
      ) : (
        <div className="d-flex">
          <div className="candidate-upload pre-screening">
            <div>
              <div className="pre-screening-heading">
                <div className="d-flex justify-content-center">
                  <img
                    src="/images/gappeoai-logo.png"
                    alt=""
                    width={262}
                    height={56.5}
                  />
                </div>
                <h3>Job Prescreening</h3>
              </div>
              {answeringCompleted ? (
                <div className="thank-you-screen">
                  <div className="d-flex justify-content-center"><img src="/images/thankyou-icon.gif" alt="" /></div>
                  <h2>Prescreening completed</h2>
                  <p>You will receive an email notifcation for the next steps</p>
                  <div className="d-flex justify-content-center"><button className="close-btn" onClick={() => navigate('/login')}>Close Window</button></div>
                </div>
              ) : (
                <>
                  <div className="candidate-upload-header">
                    <div className="candidate-upload-title">
                      <div className="position-details">
                        <div className="d-flex justify-content-between">
                          <h3>{job?.title}</h3>
                          <button onClick={() => setShowJobDetails((prev) => !prev)}>
                            {showJobDetails ? "Hide job details" : "View job details"}
                          </button>
                        </div>
                        <div className="job-details">
                          <p>Job location: {job?.city?.name}</p>
                          <p>Company: {job?.company_name}</p>
                        </div>
                        <div className="job-details">
                          <p>Experience: {job?.experience_required}</p>
                        </div>
                        <div className="requirements">
                          {job?.skills?.slice(0, 2).map((skills, index) => (
                            <p className="candidate-skills" key={index}>
                              {skills}
                            </p>
                          ))}
                          {job?.skills?.length > 2 && (
                            <p className="candidate-skills more-skills">
                              +{job?.skills.length - 2} more
                              <div className="skills-popup">
                                {job?.skills.slice(2).map((s, idx, arr) => (
                                  <span key={idx}>
                                    {s}
                                    {idx < arr.length - 1 && ", "}
                                  </span>
                                ))}
                              </div>
                            </p>
                          )}
                          {job?.salary && (
                            <p
                              className="candidate-skills"
                              style={{
                                background: "#DEF7EC",
                                color: "#03543F",
                                width: "unset",
                                border: "1px solid #4CC948",
                              }}
                            >
                              {job?.salary}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="candidate-upload-body">
                    <div className="questions-form">
                      <h4>Answer the below questions</h4>
                    </div>
                    <div className="job-details-body">
                      <div className="question-section">
                        {qualifyQuestions.length > 0 && (
                          <div className="qualifying-question-list">
                            {qualifyQuestions.map((q, index) => {
                              const type =
                                QUESTION_TYPE_ID_TO_LABEL[q.question_type_id];
                              return (
                                <div
                                  className="qualifying-question-item"
                                  key={q.id}
                                >
                                  <h5>{index + 1}. {q.question_text}</h5>

                                  {(() => {
                                    switch (type) {
                                      case "MCQ":
                                        return (
                                          <>
                                            <select
                                              className="answer-select"
                                              value={
                                                answers[q.id]?.selected_option_id ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const selectedOption =
                                                  q.options.find(
                                                    (opt) =>
                                                      opt.id === e.target.value
                                                  );
                                                handleOptionChange(
                                                  q.id,
                                                  selectedOption?.id,
                                                  selectedOption?.option_text
                                                );
                                              }}
                                            >
                                              <option value="">
                                                Select option
                                              </option>
                                              {q.options.map((opt) => (
                                                <option key={opt.id} value={opt.id}>
                                                  {opt.option_text}
                                                </option>
                                              ))}
                                            </select>
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      case "Numeric":
                                        return (
                                          <>
                                            <input
                                              className="qualifying-question-number-input"
                                              type="number"
                                              value={
                                                answers[q.id]?.answer_text || ""
                                              }
                                              onChange={(e) =>
                                                handleOptionChange(
                                                  q.id,
                                                  null,
                                                  e.target.value
                                                )
                                              }
                                              placeholder="Enter number"
                                            />
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      case "Yes/No":
                                        return (
                                          <>
                                            <div className="yes-no-buttons">
                                              {q.options.map((opt) => (
                                                <button
                                                  key={opt.id}
                                                  type="button"
                                                  className={`yes-no-btn ${
                                                    answers[q.id]
                                                      ?.selected_option_id ===
                                                    opt.id
                                                      ? "active"
                                                      : ""
                                                  }`}
                                                  onClick={() =>
                                                    handleOptionChange(
                                                      q.id,
                                                      opt.id,
                                                      opt.option_text
                                                    )
                                                  }
                                                >
                                                  {opt.option_text}
                                                </button>
                                              ))}
                                            </div>
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      case "Free Text":
                                        return (
                                          <>
                                            <input
                                              className="qualifying-question-text-input"
                                              type="text"
                                              maxLength={50}
                                              value={
                                                answers[q.id]?.answer_text || ""
                                              }
                                              placeholder="Enter your response"
                                              onChange={(e) =>
                                                handleOptionChange(
                                                  q.id,
                                                  null,
                                                  e.target.value
                                                )
                                              }
                                            />
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      default:
                                        return (
                                          <>
                                            <textarea
                                              placeholder="Enter your response"
                                              value={
                                                answers[q.id]?.answer_text || ""
                                              }
                                              onChange={(e) =>
                                                handleOptionChange(
                                                  q.id,
                                                  null,
                                                  e.target.value
                                                )
                                              }
                                            />
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );
                                    }
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="qualifying-question-btn">
                    <button onClick={submitAnswers}>Submit</button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={`pre-screening-job-details ${showJobDetails ? "open" : ""}`}>
            <h3>{job?.title}</h3>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Company details</h6>
                <p>{job?.company_name}</p>
              </div>
              <div>
                <h6>Employment type</h6>
                <p>{job?.job_type?.name}</p>
              </div>
            </div>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Salary range</h6>
                <p>{job?.salary}</p>
              </div>
              <div>
                <h6>Location</h6>
                <p>{job?.country?.name}, {job?.city?.name}</p>
              </div>
            </div>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Experience</h6>
                <p>{job?.experience_required}</p>
              </div>
            </div>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Skills</h6>
                <p>{job?.skills?.map((skills, index) => (
                    <p key={index}>{skills},</p>
                  ))}
                </p>
              </div>
            </div>
            <div>
              <div>
                <h6>Description</h6>
                <p className="description-text">{job?.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
